/**
 * Jun-2026 (User-Wunsch) — HSN/TSN nachschlagen.
 *
 * EIN Popup, KEINE Tabs. Ein einziges Suchfeld durchsucht kombiniert
 * Hersteller / Modell / HSN / TSN in der internen Fahrzeugdatenbank
 * (KBA-Verzeichnis Klasse L + Piaggio/GOVECS).
 *
 * Zusätzlich zwei Eingabefelder — Erstzulassung und Kaufdatum (jeweils nur
 * Monat + Jahr) — die beim Übernehmen mit ins Inserat übergeben werden.
 */
import { useState, useCallback, useEffect } from "react";
import { Search, Loader2, Info, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";

const FUEL_LABELS = { "0001": "Benzin", "0002": "Diesel", "0004": "Elektro", "0100": "sonstige" };

function ResultCard({ r, onPick, picking }) {
    return (
        <div className="border rounded-md p-3 bg-white hover:border-[#FFCC00] transition-colors" data-testid={`hsn-tsn-result-${r.hsn}-${r.tsn}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold bg-gray-900 text-[#FFCC00] px-2 py-0.5 rounded-sm">{r.hsn}</span>
                        <span className="font-mono text-sm font-bold bg-gray-100 text-gray-800 px-2 py-0.5 rounded-sm">{r.tsn}</span>
                        {r.fahrzeugklasse && <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-sm">{r.fahrzeugklasse}</span>}
                    </div>
                    <div className="mt-1.5 text-sm font-semibold text-gray-900 truncate">{r.hersteller || "—"}</div>
                    <div className="text-sm text-gray-700">{r.bezeichnung || "—"}</div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                        {r.ccm ? <span>{r.ccm} cm³</span> : null}
                        {r.kw ? <span>{r.kw} kW</span> : null}
                        {r.fuel_code ? <span>{FUEL_LABELS[r.fuel_code] || r.fuel_code}</span> : null}
                        {r.erstzulassung_ab ? <span>ab {r.erstzulassung_ab}</span> : null}
                    </div>
                </div>
                {onPick && (
                    <Button type="button" size="sm" disabled={picking} onClick={() => onPick(r)} className="shrink-0 h-8 rounded-sm bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] text-xs font-bold" data-testid={`hsn-tsn-pick-${r.hsn}-${r.tsn}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Übernehmen
                    </Button>
                )}
            </div>
        </div>
    );
}

export default function HsnTsnLookupModal({ open, onOpenChange, onPick }) {
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [stats, setStats] = useState(null);
    // Nur Monat + Jahr (type=month liefert "YYYY-MM").
    const [erstzulassung, setErstzulassung] = useState("");
    const [kaufdatum, setKaufdatum] = useState("");

    useEffect(() => {
        if (!open) return;
        setResults([]); setNotFound(false); setQ("");
        api.get("/hsn-tsn/stats").then((res) => setStats(res.data)).catch(() => {});
    }, [open]);

    const doSearch = useCallback(async () => {
        if (q.trim().length < 2) { toast.error("Bitte mind. 2 Zeichen eingeben (Hersteller, Modell oder HSN/TSN)"); return; }
        setLoading(true); setNotFound(false); setResults([]);
        try {
            const res = await api.get("/hsn-tsn/search", { params: { q: q.trim() } });
            setResults(res.data.results || []);
            setNotFound((res.data.results || []).length === 0);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fehler bei der Suche");
        } finally { setLoading(false); }
    }, [q]);

    const handlePick = useCallback((r) => {
        if (!onPick) return;
        onPick({ ...r, erstzulassung_month: erstzulassung || null, kaufdatum_month: kaufdatum || null });
        onOpenChange(false);
    }, [onPick, erstzulassung, kaufdatum, onOpenChange]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="hsn-tsn-lookup-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Search className="w-5 h-5 text-[#FFCC00]" /> HSN / TSN nachschlagen
                    </DialogTitle>
                </DialogHeader>

                {/* Ein Suchfeld für Hersteller / Modell / HSN / TSN */}
                <div className="flex items-end gap-2">
                    <div className="flex-1">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Hersteller, Modell oder HSN / TSN</label>
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="z.B. Vespa GTS, BMW R 1250, Piaggio, 4013, AML …" onKeyDown={(e) => e.key === "Enter" && doSearch()} autoFocus data-testid="hsn-tsn-input-search" />
                    </div>
                    <Button type="button" onClick={doSearch} disabled={loading} className="h-10 rounded-sm bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] font-bold" data-testid="hsn-tsn-search-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Suchen
                    </Button>
                </div>

                {/* Erstzulassung / Kaufdatum — nur Monat + Jahr; werden beim Übernehmen mitgegeben */}
                {onPick && (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Erstzulassung (Monat/Jahr)</label>
                            <Input type="month" value={erstzulassung} onChange={(e) => setErstzulassung(e.target.value)} data-testid="hsn-tsn-input-erstzulassung" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Kaufdatum (Monat/Jahr)</label>
                            <Input type="month" value={kaufdatum} onChange={(e) => setKaufdatum(e.target.value)} data-testid="hsn-tsn-input-kaufdatum" />
                        </div>
                    </div>
                )}

                <div className="space-y-2 mt-1">
                    {notFound && (
                        <div className="text-sm text-gray-500 flex items-center gap-2 py-4" data-testid="hsn-tsn-notfound">
                            <Info className="w-4 h-4" /> Kein Treffer in der HSN/TSN-Datenbank.
                        </div>
                    )}
                    {results.map((r) => (
                        <ResultCard key={`${r.hsn}-${r.tsn}`} r={r} onPick={onPick ? handlePick : null} />
                    ))}
                </div>

                {stats && (
                    <div className="text-[11px] text-gray-400 border-t pt-2 mt-1" data-testid="hsn-tsn-stats">
                        Datenbestand: {stats.total?.toLocaleString("de-DE")} Einträge · {stats.distinct_hsn} Hersteller · Quelle: {stats.quelle}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
